import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../ui/table";
import Badge from "../../ui/badge/Badge";
import { Edit, Trash2, AlertCircle, Power, PowerOff } from 'lucide-react';
import MoreButton from '../../common/MoreButton';
import { useNavigate } from "react-router";
import api from "../../../services/api";
import authService from "../../../services/authService";
import Pagination from "../../ui/pagination/Pagination";
import ComponentCard from "../../common/ComponentCard";
import Button from "../../ui/button/Button";
import Modal from '../../common/Modal';
import Alert from '../../ui/alert/Alert';

interface UtmifyIntegration {
    id: string;
    integrationId: string;
    companyId: string;
    provider: 'utmify';
    name: string;
    apiUrl: string | null;
    accessToken: string | null;
    events: string[] | null;
    productList: string[];
    offersList: string[] | null;
    active: boolean;
    createdAt: string;
    updatedAt: string;
}

export default function UtmifyTable() {
    const navigate = useNavigate();
    const companyId = authService.getCompanyId();

    const [integrations, setIntegrations] = useState<UtmifyIntegration[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: "success" | "error" | "warning" | "info";
        message: string;
        title: string;
    } | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");

    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [integrationToDelete, setIntegrationToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

    useEffect(() => {
        fetchIntegrations();
    }, []);

    const fetchIntegrations = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get(`/api/integrations/${companyId}/list/utmify`);

            if (response.data && response.data.success) {
                setIntegrations(response.data.data || []);
            } else {
                setIntegrations([]);
            }
        } catch (err: any) {
            console.error("Erro ao buscar integrações UTMify:", err);
            setError(err.message || "Erro ao carregar integrações UTMify");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (integrationId: string) => {
        navigate(`/apps/utmify/edit/${integrationId}`);
    };

    const handleToggleActive = async (integration: UtmifyIntegration) => {
        setToggleLoadingId(integration.id);
        try {
            await api.put(
                `/api/integrations/${companyId}/by-id/${integration.id}`,
                { status: integration.active ? 'inactive' : 'active' }
            );

            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: integration.active
                    ? "Integração desativada."
                    : "Integração ativada."
            });
            setTimeout(() => setAlertInfo(null), 3000);

            await fetchIntegrations();
        } catch (err) {
            console.error("Erro ao alternar status da integração:", err);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: "Não foi possível alterar o status. Tente novamente."
            });
        } finally {
            setToggleLoadingId(null);
        }
    };

    const handleDelete = async () => {
        if (!integrationToDelete) return;

        setDeleteLoading(true);
        try {
            await api.delete(`/api/integrations/${companyId}/by-id/${integrationToDelete}`);

            setDeleteModalOpen(false);
            setIntegrationToDelete(null);
            setDeleteLoading(false);

            fetchIntegrations();

            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: "Integração excluída com sucesso."
            });
            setTimeout(() => setAlertInfo(null), 3000);
        } catch (err) {
            console.error("Erro ao excluir integração UTMify:", err);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: "Erro ao excluir integração. Tente novamente."
            });
            setDeleteLoading(false);
        }
    };

    const openDeleteModal = (integrationId: string) => {
        setIntegrationToDelete(integrationId);
        setDeleteModalOpen(true);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const filteredIntegrations = integrations.filter((it) =>
        it.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredIntegrations.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredIntegrations.length / itemsPerPage);

    const formatDate = (dateString: string) => {
        if (!dateString) return '--/--/----';
        try {
            return new Date(dateString).toLocaleDateString('pt-BR');
        } catch {
            return '--/--/----';
        }
    };

    if (error) {
        return <div className="p-4 text-center dark:text-white/90">Erro: {error}</div>;
    }

    if (!loading && integrations.length === 0) {
        return (
            <ComponentCard title="Gerencie suas integrações UTMify">
                <div className="flex flex-col items-center justify-center gap-4 py-6">
                    <img
                        src="/images/integration/utmify-icon.png"
                        alt="Utmify"
                        className="w-12 h-12 object-contain opacity-70"
                    />
                    <div className="p-4 text-center dark:text-white/90">
                        Você ainda não possui integrações UTMify cadastradas.
                    </div>
                    <Button
                        size="md"
                        variant="primary"
                        onClick={() => navigate(`/apps/utmify/new-utmify`)}
                    >
                        Nova integração UTMify
                    </Button>
                </div>
            </ComponentCard>
        );
    }

    return (
        <ComponentCard title="Gerencie suas integrações UTMify">
            <div className="col-span-12 space-y-6">
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="relative w-full md:w-1/3 lg:w-1/4">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Pesquisar integração..."
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            />
                        </div>

                        <Button
                            size="md"
                            variant="primary"
                            onClick={() => navigate(`/apps/utmify/new-utmify`)}
                        >
                            Nova integração UTMify
                        </Button>
                    </div>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl">
                <div className="max-w-full overflow-x-auto">
                    <Table>
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Nome
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Produtos
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Ofertas
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Criada em
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8"
                                >
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {!loading && currentItems.map((integration) => {
                                const productCount = integration.productList?.length || 0;
                                const offerCount = integration.offersList?.length || 0;
                                return (
                                    <TableRow key={integration.id}>
                                        <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                {integration.name || 'UTMify Integration'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            <Badge
                                                size="sm"
                                                color={integration.active ? "success" : "error"}
                                            >
                                                {integration.active ? "Ativo" : "Inativo"}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {productCount === 0 ? (
                                                <Badge size="sm" color="info">Todos os produtos</Badge>
                                            ) : (
                                                <span>
                                                    {productCount === 1 ? '1 produto' : `${productCount} produtos`}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {offerCount === 0 ? (
                                                <Badge size="sm" color="info">Todas as ofertas</Badge>
                                            ) : (
                                                <span>
                                                    {offerCount === 1 ? '1 oferta' : `${offerCount} ofertas`}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {formatDate(integration.createdAt)}
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                            <div className="flex justify-end gap-2 relative">
                                                <MoreButton
                                                    options={[
                                                        {
                                                            label: "Editar",
                                                            onClick: () => handleEdit(integration.id),
                                                            icon: <Edit className="size-4" />
                                                        },
                                                        {
                                                            label: integration.active ? "Desativar" : "Ativar",
                                                            onClick: () => {
                                                                if (toggleLoadingId !== integration.id) {
                                                                    handleToggleActive(integration);
                                                                }
                                                            },
                                                            icon: integration.active
                                                                ? <PowerOff className="size-4" />
                                                                : <Power className="size-4" />,
                                                        },
                                                        {
                                                            label: "Excluir",
                                                            onClick: () => openDeleteModal(integration.id),
                                                            icon: <Trash2 className="size-4" />,
                                                            className: "text-red-600 dark:text-red-400"
                                                        }
                                                    ]}
                                                />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    {!loading && totalPages > 1 && (
                        <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {alertInfo && alertInfo.show && (
                <div className="mt-4">
                    <Alert
                        variant={alertInfo.type}
                        title={alertInfo.title}
                        message={alertInfo.message}
                    />
                </div>
            )}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                size="sm"
                onConfirm={handleDelete}
                confirmText="Excluir"
                cancelText="Cancelar"
                confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                confirmLoading={deleteLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tem certeza que deseja excluir esta integração?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Esta integração não será mais disparada para os produtos/ofertas selecionados.
                    </p>
                </div>
            </Modal>
        </ComponentCard>
    );
}
